/**
 * Drawing imported overlays: somebody else's boundaries, phase lines and
 * areas of interest.
 *
 * Three things this layer does differently from every other one on the map,
 * each because a layer is not a track.
 *
 * **It converts back.** Everything inside Parallax carries `(lat, lon)` and
 * GeoJSON — which is what MapLibre eats — wants `[lon, lat]`. The server hands
 * up lat-first, so the swap happens here, once, in `ring`. That is the second
 * and last place in the whole path where a transposition could be introduced;
 * the first is `domain/layer.position` on the way in.
 *
 * **It draws expired overlays, differently.** A lapsed phase line is not
 * deleted and not hidden: it is dimmed and dashed. Hiding it leaves an operator
 * looking at an empty map with no way to tell whether anything was ever there,
 * and a phase line that lapsed an hour ago is often exactly the thing somebody
 * is asking about.
 *
 * **It never draws an overlay as though it were ours.** Imported geometry is
 * amber and labelled with the layer it came from, so it cannot be mistaken for
 * a zone this system is enforcing. Nothing gates on a layer, and nothing on
 * screen may suggest otherwise.
 */

import { useEffect, useState } from 'react';

import { getLayers, type Layer, type LayerFeature } from '@/api/layers';
import { geoJsonSource, useMapController, type MapLike } from '@/map/MapCanvas';

export const OVERLAY_SOURCE_ID = 'external-overlays';
const OVERLAY_FILL_LAYER = 'external-overlays-fill';
const OVERLAY_LINE_LAYER = 'external-overlays-line';
const OVERLAY_EXPIRED_LINE_LAYER = 'external-overlays-expired-line';
const OVERLAY_POINT_LAYER = 'external-overlays-point';
const OVERLAY_LABEL_LAYER = 'external-overlays-label';

/** Amber: not ours, not enforced, and not to be read as either. */
const OVERLAY_COLOR = '#e0a33e';

/**
 * `(lat, lon)` vertices as a GeoJSON position ring.
 *
 * The conversion, and the reason it is a named function rather than an inline
 * `.map`: a transposition here is invisible — it produces a well-formed ring
 * that draws somewhere else entirely — so it is worth having one place to look
 * and one place to test.
 *
 * `close` repeats the first position, which GeoJSON requires of a polygon ring
 * and which the server deliberately does not store.
 */
export function ring(
  vertices: Array<[number, number]>,
  close = false,
): Array<[number, number]> {
  const positions: Array<[number, number]> = vertices.map(([lat, lon]) => [lon, lat]);
  if (!close || positions.length < 3) return positions;
  const first = positions[0];
  const last = positions[positions.length - 1];
  if (first && last && (first[0] !== last[0] || first[1] !== last[1])) {
    positions.push(first);
  }
  return positions;
}

/** One imported feature as something MapLibre can draw. */
export function overlayFeature(layer: Layer, feature: LayerFeature): unknown {
  const properties = {
    layerId: layer.layerId,
    layerName: layer.name,
    name: feature.name,
    expired: layer.expired,
    kind: feature.kind,
  };
  const vertices = feature.vertices as Array<[number, number]>;

  if (feature.kind === 'point') {
    const [position] = ring(vertices);
    return { type: 'Feature', properties, geometry: { type: 'Point', coordinates: position } };
  }
  if (feature.kind === 'line') {
    return {
      type: 'Feature',
      properties,
      geometry: { type: 'LineString', coordinates: ring(vertices) },
    };
  }
  return {
    type: 'Feature',
    properties,
    geometry: { type: 'Polygon', coordinates: [ring(vertices, true)] },
  };
}

export function overlayCollection(layers: Layer[]): unknown {
  const features = layers.flatMap((layer) =>
    layer.features.map((feature) => overlayFeature(layer, feature)),
  );
  return { type: 'FeatureCollection', features };
}

export interface OverlayLayer {
  draw: (layers: Layer[]) => void;
  destroy: () => void;
}

export function createOverlayLayer(map: MapLike): OverlayLayer {
  if (!map.getSource(OVERLAY_SOURCE_ID)) {
    map.addSource(OVERLAY_SOURCE_ID, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
  }

  if (!map.getLayer(OVERLAY_FILL_LAYER)) {
    map.addLayer({
      id: OVERLAY_FILL_LAYER,
      type: 'fill',
      source: OVERLAY_SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'area'],
      paint: {
        'fill-color': OVERLAY_COLOR,
        // Lighter than an operating zone's fill. An imported area is context,
        // not a constraint this system enforces, and it must not be the most
        // prominent thing on a map somebody is planning a route across.
        'fill-opacity': ['case', ['get', 'expired'], 0.03, 0.08],
      },
    });
  }

  // Two line layers rather than one, because `line-dasharray` is not
  // data-driven in MapLibre: a per-feature dash has to be a per-layer dash
  // behind a filter. Same reason useZoneLayer splits keep-in from keep-out.
  if (!map.getLayer(OVERLAY_LINE_LAYER)) {
    map.addLayer({
      id: OVERLAY_LINE_LAYER,
      type: 'line',
      source: OVERLAY_SOURCE_ID,
      filter: ['!', ['get', 'expired']],
      paint: { 'line-color': OVERLAY_COLOR, 'line-width': 1.6 },
    });
  }

  if (!map.getLayer(OVERLAY_EXPIRED_LINE_LAYER)) {
    map.addLayer({
      id: OVERLAY_EXPIRED_LINE_LAYER,
      type: 'line',
      source: OVERLAY_SOURCE_ID,
      filter: ['get', 'expired'],
      // Dimmed and dashed rather than removed. "This lapsed yesterday" is more
      // use to an operator than an empty map they cannot interpret.
      paint: {
        'line-color': OVERLAY_COLOR,
        'line-width': 1.2,
        'line-opacity': 0.45,
        'line-dasharray': [2, 2],
      },
    });
  }

  if (!map.getLayer(OVERLAY_POINT_LAYER)) {
    map.addLayer({
      id: OVERLAY_POINT_LAYER,
      type: 'circle',
      source: OVERLAY_SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'point'],
      paint: {
        'circle-radius': 4,
        'circle-color': OVERLAY_COLOR,
        'circle-opacity': ['case', ['get', 'expired'], 0.4, 0.9],
        'circle-stroke-width': 1,
        'circle-stroke-color': 'rgba(0,0,0,0.6)',
      },
    });
  }

  if (!map.getLayer(OVERLAY_LABEL_LAYER)) {
    map.addLayer({
      id: OVERLAY_LABEL_LAYER,
      type: 'symbol',
      source: OVERLAY_SOURCE_ID,
      layout: {
        // The layer's name travels with every feature, so an imported boundary
        // can always be traced back to the file it came from without clicking.
        'text-field': [
          'case',
          ['!=', ['get', 'name'], ''],
          ['get', 'name'],
          ['get', 'layerName'],
        ],
        'text-size': 10,
        'text-offset': [0, -0.9],
      },
      paint: {
        'text-color': OVERLAY_COLOR,
        'text-opacity': ['case', ['get', 'expired'], 0.5, 1],
        'text-halo-color': 'rgba(0,0,0,0.85)',
        'text-halo-width': 1.2,
      },
    });
  }

  return {
    draw(layers: Layer[]) {
      geoJsonSource(map, OVERLAY_SOURCE_ID)?.setData(overlayCollection(layers));
    },
    destroy() {
      for (const id of [
        OVERLAY_LABEL_LAYER,
        OVERLAY_POINT_LAYER,
        OVERLAY_EXPIRED_LINE_LAYER,
        OVERLAY_LINE_LAYER,
        OVERLAY_FILL_LAYER,
      ]) {
        if (map.getLayer(id)) map.removeLayer(id);
      }
      if (map.getSource(OVERLAY_SOURCE_ID)) map.removeSource(OVERLAY_SOURCE_ID);
    },
  };
}


/**
 * How often the overlay list is re-read.
 *
 * Slow on purpose. A layer does not move and does not decay, so the only
 * things that change this list are an operator importing or deleting one —
 * events measured in minutes, not in frames. Polling it at track rate would
 * cost the map redraws to discover nothing.
 */
const OVERLAY_POLL_MS = 15_000;

export function useOverlayLayer(): void {
  const controller = useMapController();
  const [layers, setLayers] = useState<Layer[]>([]);

  useEffect(() => {
    let live = true;
    const load = (): void => {
      void getLayers().then((result) => {
        // A failed poll leaves the previous overlays alone. Blanking every
        // boundary because one request timed out is a mass disappearance
        // caused by our own network, which is the lie the track layer refuses
        // for the same reason.
        if (live && result.ok) setLayers(result.data.layers);
      });
    };
    load();
    const timer = window.setInterval(load, OVERLAY_POLL_MS);
    return () => {
      live = false;
      window.clearInterval(timer);
    };
  }, []);

  useEffect(
    () =>
      controller.ready((map) => {
        const layer = createOverlayLayer(map);
        layer.draw(layers);
        return () => layer.destroy();
      }),
    [controller, layers],
  );
}
